import { Card } from "@amzn/stencil-react-components/card";
import { Checkbox } from "@amzn/stencil-react-components/checkbox";
import { InputHeader } from "@amzn/stencil-react-components/input";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { MessageBanner } from "@amzn/stencil-react-components/message-banner";
import { Select } from "@amzn/stencil-react-components/select";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { useCheckoutForm } from "@/pages/checkout/hooks/use-checkout-form";
import { useCheckoutLocations } from "@/pages/checkout/hooks/use-checkout-locations";
import { FormField } from "@/common/components/FormField/form-field";
import { SelectOrInputField } from "@/common/components/SelectOrInputField/select-or-input-field";
import React, { useEffect } from "react";
import { useSession } from "@/providers/session-provider";
import { useUserInfo } from "@/providers/user-info-provider";

const STRING_PATH = "checkout.formPanel.shippingDetailsCard";

export const ChangeShippingDetailsCard: React.FC = () => {
  const { username } = useSession();
  const { form, validationErrors, updateField } = useCheckoutForm();
  const amazonianInfo = useUserInfo();

  useEffect(() => {
    if (!form.fullName && amazonianInfo?.basicInfo) {
      const name = `${amazonianInfo.basicInfo.firstName ?? ""} ${amazonianInfo.basicInfo.lastName ?? ""}`.trim();
      if (name) {
        updateField("fullName", name);
      }
    }
  }, [amazonianInfo]);

  const {
    countries,
    phoneCodes,
    states,
    cities,
    isStateDisabled,
    isCityDisabled,
    useTextInputStates,
    useTextInputCities,
    handleCountryChange,
    handleStateChange,
    handleCityChange,
  } = useCheckoutLocations();

  return (
    <Card data-testid="shipping-details-card" padding="S300" width="100%">
      <Col gridGap="S300" width="100%">
        {/* Title */}
        <Col>
          <Text fontSize="T400" fontWeight="bold" color="neutral90">
            {getString(`${STRING_PATH}.step2`)}
          </Text>
          <Text fontSize="T200" color="neutral90">
            {getString(`${STRING_PATH}.description`)}
          </Text>
        </Col>

        {/* Address fields */}
        <Col gridGap="S200" width="85%">
          <FormField
            testId="alias-input"
            label={getString(`${STRING_PATH}.alias`)}
            value={username ?? ""}
            required
            disabled
          />

          <FormField
            testId="full-name-input"
            label={getString(`${STRING_PATH}.fullName`)}
            value={form.fullName}
            required
            onChange={(value) => updateField("fullName", value)}
          />

          {/* Country */}
          <Col gridGap="S100">
            <InputHeader
              labelText={getString(`${STRING_PATH}.country`)}
              htmlFor="select-country"
              required
            />
            <Select
              id="select-country"
              dataTestId="country-input"
              isSearchable
              isMulti={false}
              width="73%"
              options={countries.map((c) => c.name)}
              value={form.country}
              onChange={(selectedValue) => handleCountryChange(selectedValue ?? "")}
            />
          </Col>

          <FormField
            testId="address-line1-input"
            label={getString(`${STRING_PATH}.addressLine1`)}
            description={getString(`${STRING_PATH}.line1description`)}
            value={form.addressLine1}
            required
            onChange={(value) => updateField("addressLine1", value)}
          />

          <FormField
            testId="address-line2-input"
            label={getString(`${STRING_PATH}.addressLine2`)}
            description={getString(`${STRING_PATH}.line2description`)}
            value={form.addressLine2}
            onChange={(value) => updateField("addressLine2", value)}
          />

          {/* State and City */}
          <Row gridGap="S300">
            <Col gridGap="S100" width="50%">
              <SelectOrInputField
                label={getString(`${STRING_PATH}.stateProvinceRegion`)}
                testId="state-input"
                value={form.citySubdivision1}
                options={states}
                useTextInput={useTextInputStates}
                disabled={isStateDisabled}
                required
                noOptionsText={getString(`${STRING_PATH}.noOptionsText`)}
                onTextChange={(value) => updateField("citySubdivision1", value)}
                onSelectChange={handleStateChange}
              />
            </Col>
            <Col gridGap="S100" width="50%">
              <SelectOrInputField
                label={getString(`${STRING_PATH}.city`)}
                testId="city-input"
                value={form.city}
                options={cities}
                useTextInput={useTextInputCities}
                disabled={isCityDisabled}
                required
                noOptionsText={getString(`${STRING_PATH}.noOptionsText`)}
                onTextChange={(value) => updateField("city", value)}
                onSelectChange={handleCityChange}
              />
            </Col>
          </Row>

          <Col width={301}>
            <FormField
              testId="postal-code-input"
              label={getString(`${STRING_PATH}.zipCode`)}
              value={form.postalCode}
              required
              onChange={(value) => updateField("postalCode", value)}
            />
          </Col>

          {/* Country code and Phone number */}
          <Row gridGap="S300">
            <Col gridGap="S200" width={150}>
              <InputHeader
                labelText={getString(`${STRING_PATH}.countryCode`)}
                htmlFor="select-countrycode"
                required
              />
              <Select
                id="select-countrycode"
                dataTestId="select-countrycode"
                labelId="select-countrycode"
                isSearchable
                isMulti={false}
                placeholder="code"
                options={phoneCodes}
                value={`+ ${form.countryCode}`}
                onChange={(selectedValue) => updateField("countryCode", selectedValue?.replace(/^\+\s*/, "") ?? "")}
              />
            </Col>
            <Col width="100%">
              <FormField
                testId="phone-number-input"
                label={getString(`${STRING_PATH}.phoneNumber`)}
                value={form.phoneNumber}
                required
                error={validationErrors.phoneNumber}
                onChange={(value) => updateField("phoneNumber", value)}
              />
            </Col>
          </Row>

          {/* Banner */}
          <MessageBanner>{getString(`${STRING_PATH}.banner`)}</MessageBanner>
        </Col>

        <Col width="85%">
          <FormField
            testId="delivery-instructions-input"
            label={getString(`${STRING_PATH}.deliveryInstructions`)}
            value={form.deliveryInstructions}
            onChange={(value) => updateField("deliveryInstructions", value)}
            placeholder={getString(`${STRING_PATH}.placeholderDeliveryInstructions`)}
            isTextArea
          />
        </Col>

        <Row gridGap="S200">
          <Checkbox
            data-testid="address-confirmation-checkbox"
            checked={form.shippingDetailsConfirmationCheckbox}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              updateField("shippingDetailsConfirmationCheckbox", event.target.checked)
            }
          />
          <Label>{getString(`${STRING_PATH}.shippingDetailsConfirmation`)}</Label>
        </Row>
      </Col>
    </Card>
  );
};
